<div id="content">
  <div class="page-title d-flex align-items-center justify-content-between flex-wrap mb-3">
    <div class="d-flex align-items-center flex-wrap gap-2">
      <a mat-button [routerLink]="back_route()">
        <mat-icon>arrow_back</mat-icon>
        {{ back_label() }}
      </a>
      <h1 class="mb-0">{{ page_title }}</h1>
    </div>
    <div class="d-flex flex-wrap gap-2 align-items-center">
      @if (show_workflow_actions()) {
        <button
          mat-stroked-button
          type="button"
          [disabled]="workflow_loading() || is_processing() || exporting_pdf()"
          (click)="exportPdf()"
        >
          <mat-icon>picture_as_pdf</mat-icon>
          Exportar PDF
        </button>
        @if (has_imported_scanned_pdf()) {
          <button
            mat-stroked-button
            type="button"
            [disabled]="workflow_loading() || is_processing() || exporting_pdf()"
            (click)="downloadImportedScannedPdf()"
          >
            <mat-icon>draw</mat-icon>
            Descargar examen escaneado
          </button>
        }
        @if (can_import_pdf()) {
          <input #import_pdf_input type="file" accept="application/pdf" hidden (change)="onImportPdfSelected($event)" />
          <button mat-stroked-button type="button" [disabled]="workflow_loading() || is_processing()" (click)="import_pdf_input.click()">
            <mat-icon>upload_file</mat-icon>
            Importar examen
          </button>
        }
        @if (can_show_correct_button()) {
          <button
            mat-flat-button
            color="accent"
            type="button"
            [disabled]="workflow_loading() || is_processing()"
            (click)="startCorrection()"
          >
            Corregir
          </button>
        }
      }
      @if (show_save_button()) {
        <button mat-flat-button color="accent" type="button" [disabled]="saving() || loading() || form.invalid" (click)="save()">
          Guardar
        </button>
      }
    </div>
  </div>

  @if (loading()) {
    <p>Cargando...</p>
  } @else {
    @if (is_processing()) {
      <div class="processing-banner card p-3 mb-3 d-flex align-items-center gap-2">
        <mat-spinner diameter="24"></mat-spinner>
        <span>Procesando… Las notas se actualizarán automáticamente.</span>
      </div>
    }

    <form [formGroup]="form" class="evaluation-form card p-3">
      <div class="form-row">
        @if (is_create_mode()) {
          <mat-form-field class="full-width">
            <mat-label>Empleado evaluado</mat-label>
            <mat-select formControlName="user_id">
              @for (worker of workers(); track worker.id) {
                <mat-option [value]="worker.id">{{ getWorkerLabel(worker) }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <div class="status-summary">
            <p><strong>Estado:</strong> {{ status_label() }}</p>
          </div>
        } @else {
          <div class="evaluation-summary-grid">
            <p><strong>Empleado evaluado:</strong> {{ evaluated_user_label() }}</p>
            <p><strong>Estado:</strong> {{ status_label() }}</p>
            <p><strong>Nota global:</strong> {{ global_score_label() }}</p>
          </div>
        }
      </div>

      @if (can_edit_answers()) {
        <p class="draft-hint">
          @if (is_create_mode()) {
            Selecciona el empleado y guarda para crear la evaluación. Después podrás completar respuestas, importar un PDF o corregir.
          } @else {
            Guarda las respuestas en borrador o tras importar un PDF. Cuando estén completas, pulsa «Corregir» para calcular las notas.
          }
        </p>
      }

      <h2>Respuestas</h2>

      @for (answer_meta of answer_items_meta(); track answer_meta.exam_instance_item.id; let answer_index = $index) {
        <div class="answer-block" [formGroup]="$any(answers_form_array.at(answer_index))">
          <div class="answer-header">
            <h3>{{ answer_index + 1 }}. {{ answer_meta.exam_instance_item.question_body }}</h3>
            @if (!is_create_mode()) {
              <span class="answer-score">Nota: {{ answer_meta.score_label }}</span>
            }
          </div>

          @if (answer_meta.question_type === SkillQuestionTypeEnum.Text) {
            <mat-form-field class="full-width">
              <mat-label>Respuesta</mat-label>
              <textarea matInput formControlName="text_answer" [rows]="answer_meta.exam_instance_item.answer_lines ?? 4"></textarea>
            </mat-form-field>

            @if (
              can_show_rubric_grading() &&
              answer_meta.rubric_grading_result &&
              answer_meta.rubric_grading_result.dimension_gradings.length > 0
            ) {
              <div class="rubric-grading">
                <p class="rubric-grading-title">Clasificación según rúbrica</p>
                @if (answer_meta.rubric_grading_result.requires_review) {
                  <p class="rubric-grading-review">Marcada para revisión manual</p>
                }
                <ul class="rubric-grading-list">
                  @for (dimension_grading of answer_meta.rubric_grading_result.dimension_gradings; track dimension_grading.dimension_id) {
                    <li>
                      <strong>{{ dimension_grading.dimension_name }}</strong
                      >: {{ dimension_grading.level_name }}
                    </li>
                  }
                </ul>
              </div>
            }
          }

          @if (answer_meta.question_type === SkillQuestionTypeEnum.TestSingle) {
            @let selected_option_indexes = answers_form_array.at(answer_index).get('selected_option_indexes')?.value ?? [];
            <div class="radio-options">
              @for (option of answer_meta.options_snapshot; track $index; let option_index = $index) {
                <mat-radio-button
                  name="answer_{{ answer_meta.exam_instance_item.id }}"
                  [checked]="selected_option_indexes[0] === option_index"
                  [disabled]="!can_edit_answers()"
                  (change)="setSingleOption(answer_index, option_index)"
                >
                  {{ option.label }}
                </mat-radio-button>
              }
            </div>
          }

          @if (answer_meta.question_type === SkillQuestionTypeEnum.TestMultiple) {
            @let selected_option_indexes = answers_form_array.at(answer_index).get('selected_option_indexes')?.value ?? [];
            <div class="checkbox-options">
              @for (option of answer_meta.options_snapshot; track $index; let option_index = $index) {
                <mat-checkbox
                  [checked]="selected_option_indexes.includes(option_index)"
                  [disabled]="!can_edit_answers()"
                  (change)="toggleMultipleOption(answer_index, option_index, $event.checked)"
                >
                  {{ option.label }}
                </mat-checkbox>
              }
            </div>
          }
        </div>
      }
    </form>
  }
</div>
